/**
 * dt-select.js
 *
 * Progressive-enhancement custom dropdown for every native
 * `<select class="select__select">` in the theme (desktop "Sort by",
 * mobile facets sort, and the variant-picker dropdown).
 *
 * Why: the native <select> popup is painted by the OS and its list is
 * narrower than the styled box the shopper clicks — that geometry isn't
 * stylable with CSS. This builds a fully-styled trigger + listbox on top,
 * while leaving the original <select> in the DOM (visually clipped) so
 * the surrounding <form> still submits and Dawn's own JS (FacetFiltersForm,
 * VariantSelects, ...) keeps working exactly as before.
 *
 * No dependencies, no build step. Safe to include on any page, including
 * ones with no selects at all, and safe if the script tag ends up on the
 * page twice.
 */
(function () {
  'use strict';

  if (window.__dtSelectInit) return;
  window.__dtSelectInit = true;

  var SELECT_QUERY = 'select.select__select';
  var ENHANCED_ATTR = 'data-dt-select-enhanced';
  var uid = 0;

  function nextId(prefix) {
    uid += 1;
    return prefix + '-' + uid;
  }

  // Attribute-value selector escaping for arbitrary select ids.
  function idSelector(id) {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(id);
    }
    return String(id).replace(/([^a-zA-Z0-9_-])/g, '\\$1');
  }

  function enhance(select) {
    if (!select || select.hasAttribute(ENHANCED_ATTR)) return;
    select.setAttribute(ENHANCED_ATTR, 'true');

    if (!select.id) {
      select.id = nextId('dt-select-native');
    }

    // Locate (or, failing that, create) the wrapping box. Every instance
    // of select.select__select in this theme is wrapped in a Dawn
    // `<div class="select">`, but we don't rely on that being guaranteed.
    var container = select.closest('.select');
    if (!container) {
      container = document.createElement('div');
      container.className = 'select';
      select.parentNode.insertBefore(container, select);
      container.appendChild(select);
    }
    container.classList.add('dt-select');

    // Find the associated <label for="...">, if any, so the trigger's
    // accessible name matches what a sighted user reads next to the box.
    var label = null;
    try {
      label = document.querySelector('label[for="' + idSelector(select.id) + '"]');
    } catch (err) {
      label = null;
    }
    if (label && !label.id) {
      label.id = nextId('dt-select-label');
    }

    // Some selects (e.g. the swatch variant dropdown) have a decorative
    // element living inside the wrapper alongside the <select> — move it
    // into the trigger instead of leaving it as a flex sibling, otherwise
    // it eats into the trigger's width and the panel (which matches the
    // *container's* width) stops lining up with the trigger exactly.
    var prefixNodes = Array.prototype.filter.call(container.children, function (child) {
      return child !== select && !child.classList.contains('svg-wrapper');
    });

    var triggerId = nextId('dt-select-trigger');
    var panelId = nextId('dt-select-panel');

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'dt-select__trigger';
    trigger.id = triggerId;
    trigger.setAttribute('role', 'combobox');
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', panelId);
    if (label) {
      trigger.setAttribute('aria-labelledby', label.id + ' ' + triggerId);
    } else {
      trigger.setAttribute('aria-label', select.getAttribute('aria-label') || select.name || 'Select');
    }
    if (select.disabled) trigger.disabled = true;

    if (prefixNodes.length) {
      trigger.classList.add('dt-select__trigger--has-prefix');
      prefixNodes.forEach(function (node) {
        trigger.appendChild(node);
      });
    }

    var valueSpan = document.createElement('span');
    valueSpan.className = 'dt-select__value';
    trigger.appendChild(valueSpan);

    var arrow = document.createElement('span');
    arrow.className = 'dt-select__arrow';
    arrow.setAttribute('aria-hidden', 'true');
    trigger.appendChild(arrow);

    var panel = document.createElement('div');
    panel.className = 'dt-select__panel';
    panel.id = panelId;
    panel.setAttribute('role', 'listbox');
    panel.tabIndex = -1;
    if (label) panel.setAttribute('aria-labelledby', label.id);
    panel.hidden = true;

    var optionEls = [];

    function buildOptions() {
      panel.innerHTML = '';
      optionEls = [];
      Array.prototype.forEach.call(select.options, function (opt, index) {
        var item = document.createElement('div');
        item.className = 'dt-select__option';
        item.setAttribute('role', 'option');
        item.id = panelId + '-opt-' + index;
        item.dataset.index = String(index);
        item.textContent = opt.textContent;
        item.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
        if (opt.disabled) item.setAttribute('aria-disabled', 'true');
        panel.appendChild(item);
        optionEls.push(item);
      });
    }

    function syncFromSelect() {
      var opt = select.options[select.selectedIndex];
      valueSpan.textContent = opt ? opt.textContent.trim() : '';
      optionEls.forEach(function (item, index) {
        item.setAttribute('aria-selected', index === select.selectedIndex ? 'true' : 'false');
      });
    }

    buildOptions();
    syncFromSelect();

    // Native select stays in the DOM (form submission, Dawn's own JS keep
    // working against it) but is visually clipped and out of the tab order.
    select.classList.add('dt-select__native');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');

    container.appendChild(trigger);
    container.appendChild(panel);

    var isOpen = false;
    var activeIndex = -1;
    var typeAheadBuffer = '';
    var typeAheadTimer = null;

    function firstEnabledIndex() {
      for (var i = 0; i < optionEls.length; i++) {
        if (optionEls[i].getAttribute('aria-disabled') !== 'true') return i;
      }
      return -1;
    }

    function lastEnabledIndex() {
      for (var i = optionEls.length - 1; i >= 0; i--) {
        if (optionEls[i].getAttribute('aria-disabled') !== 'true') return i;
      }
      return -1;
    }

    function setActiveOption(index, opts) {
      if (index < 0 || index >= optionEls.length) return;
      if (activeIndex > -1 && optionEls[activeIndex]) {
        optionEls[activeIndex].classList.remove('dt-select__option--active');
      }
      activeIndex = index;
      var el = optionEls[activeIndex];
      el.classList.add('dt-select__option--active');
      trigger.setAttribute('aria-activedescendant', el.id);
      if (opts && opts.scroll) {
        el.scrollIntoView({ block: 'nearest' });
      }
    }

    function moveActive(delta) {
      var len = optionEls.length;
      if (!len) return;
      var idx = activeIndex < 0 ? 0 : activeIndex;
      for (var i = 0; i < len; i++) {
        idx = (idx + delta + len) % len;
        if (optionEls[idx].getAttribute('aria-disabled') !== 'true') {
          setActiveOption(idx, { scroll: true });
          return;
        }
      }
    }

    function typeAhead(char) {
      typeAheadBuffer += char.toLowerCase();
      window.clearTimeout(typeAheadTimer);
      typeAheadTimer = window.setTimeout(function () {
        typeAheadBuffer = '';
      }, 600);

      var len = optionEls.length;
      if (!len) return;
      var start = activeIndex < 0 ? 0 : activeIndex;
      for (var i = 1; i <= len; i++) {
        var idx = (start + i) % len;
        var el = optionEls[idx];
        if (el.getAttribute('aria-disabled') === 'true') continue;
        if (el.textContent.trim().toLowerCase().indexOf(typeAheadBuffer) === 0) {
          setActiveOption(idx, { scroll: true });
          return;
        }
      }
    }

    function positionPanel() {
      container.classList.remove('dt-select--up');
      panel.style.maxHeight = '';

      var rect = trigger.getBoundingClientRect();
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      var margin = 8;
      var minSpace = 160;
      var spaceBelow = viewportHeight - rect.bottom;
      var spaceAbove = rect.top;

      var openUp = spaceBelow < minSpace && spaceAbove > spaceBelow;
      if (openUp) container.classList.add('dt-select--up');

      var available = (openUp ? spaceAbove : spaceBelow) - margin;
      if (available > 0) {
        panel.style.maxHeight = Math.max(available, 80) + 'px';
      }
    }

    function onDocumentClick(event) {
      if (container.contains(event.target)) return;
      closePanel();
    }

    function onWindowScroll(event) {
      if (container.contains(event.target)) return;
      closePanel();
    }

    function onWindowResize() {
      if (!isOpen) return;
      positionPanel();
    }

    function openPanel() {
      if (isOpen || trigger.disabled) return;
      isOpen = true;
      panel.hidden = false;
      container.classList.add('dt-select--open');
      trigger.setAttribute('aria-expanded', 'true');
      positionPanel();

      var startIndex = select.selectedIndex > -1 ? select.selectedIndex : firstEnabledIndex();
      if (startIndex > -1) setActiveOption(startIndex, { scroll: true });

      document.addEventListener('click', onDocumentClick, true);
      window.addEventListener('scroll', onWindowScroll, true);
      window.addEventListener('resize', onWindowResize);
    }

    function closePanel(opts) {
      if (!isOpen) return;
      isOpen = false;
      panel.hidden = true;
      container.classList.remove('dt-select--open', 'dt-select--up');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.removeAttribute('aria-activedescendant');
      if (activeIndex > -1 && optionEls[activeIndex]) {
        optionEls[activeIndex].classList.remove('dt-select__option--active');
      }
      activeIndex = -1;
      typeAheadBuffer = '';

      document.removeEventListener('click', onDocumentClick, true);
      window.removeEventListener('scroll', onWindowScroll, true);
      window.removeEventListener('resize', onWindowResize);

      if (opts && opts.focusTrigger) trigger.focus();
    }

    function selectOption(index) {
      var opt = select.options[index];
      if (!opt || opt.disabled) return;

      select.value = opt.value;
      // Dawn's facet form listens on the form for a bubbling 'input' event
      // to trigger its debounced AJAX re-filter/re-sort, while VariantSelects
      // listens for 'change'. Fire both so every consumer picks it up —
      // skipping either would leave one of those flows silently inert.
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));

      closePanel({ focusTrigger: true });
    }

    trigger.addEventListener('click', function () {
      if (isOpen) {
        closePanel();
      } else {
        openPanel();
      }
    });

    trigger.addEventListener('keydown', function (event) {
      var key = event.key;

      if (!isOpen) {
        if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ' || key === 'Spacebar') {
          event.preventDefault();
          openPanel();
          return;
        }
        if (key.length === 1 && /\S/.test(key)) {
          openPanel();
        } else {
          return;
        }
      }

      switch (key) {
        case 'Escape':
          event.preventDefault();
          closePanel({ focusTrigger: true });
          break;
        case 'Tab':
          closePanel();
          break;
        case 'ArrowDown':
          event.preventDefault();
          moveActive(1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          moveActive(-1);
          break;
        case 'Home':
          event.preventDefault();
          setActiveOption(firstEnabledIndex(), { scroll: true });
          break;
        case 'End':
          event.preventDefault();
          setActiveOption(lastEnabledIndex(), { scroll: true });
          break;
        case 'Enter':
        case ' ':
        case 'Spacebar':
          event.preventDefault();
          if (activeIndex > -1) selectOption(activeIndex);
          break;
        default:
          if (key.length === 1 && /\S/.test(key)) {
            event.preventDefault();
            typeAhead(key);
          }
          break;
      }
    });

    panel.addEventListener('click', function (event) {
      var item = event.target.closest ? event.target.closest('.dt-select__option') : null;
      if (!item || !panel.contains(item)) return;
      if (item.getAttribute('aria-disabled') === 'true') return;
      selectOption(Number(item.dataset.index));
    });

    panel.addEventListener('mousemove', function (event) {
      var item = event.target.closest ? event.target.closest('.dt-select__option') : null;
      if (!item || item.getAttribute('aria-disabled') === 'true') return;
      var index = Number(item.dataset.index);
      if (index !== activeIndex) setActiveOption(index);
    });

    // Redirect the existing visible <label> at our trigger — its `for`
    // still points at the now visually-hidden, unfocusable native select.
    if (label) {
      label.addEventListener('click', function (event) {
        event.preventDefault();
        trigger.focus();
        openPanel();
      });
    }

    // Keep the trigger/panel in sync with any change to the native select's
    // value, whether it came from us (selectOption above) or from something
    // else entirely (e.g. bfcache restoring a previous value).
    select.addEventListener('change', function () {
      syncFromSelect();
    });
  }

  function enhanceAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var selects = scope.querySelectorAll(SELECT_QUERY);
    Array.prototype.forEach.call(selects, enhance);
  }

  function init() {
    enhanceAll(document);

    if (!window.MutationObserver) return;

    // Dawn re-renders facet/sort sections via AJAX by replacing a
    // container's innerHTML wholesale, which wipes out anything we built.
    // Watch for that and re-enhance any fresh, not-yet-enhanced selects.
    // A microtask debounce collapses bursts of mutations (including our
    // own DOM writes above) into a single, cheap re-scan.
    var scheduled = false;
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      var raf = window.requestAnimationFrame || function (cb) {
        return window.setTimeout(cb, 0);
      };
      raf(function () {
        scheduled = false;
        enhanceAll(document);
      });
    }

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
          schedule();
          return;
        }
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
